import { z } from "zod";
import { ROUTER_PATH } from "@config/router";

/**
 * Chat `ActionButtons` contract (perplexica `action-buttons.ts`, design EDG-592 §4.3–4.5).
 * The server only emits a destination the client declared via `nav.<id>`, so the
 * capability list is derived from `CHAT_DESTINATIONS` and adding one is one entry here.
 */

export type ChatActionIntent = { kind: "route"; href: string };

const route = (href: string): ChatActionIntent => ({ kind: "route", href });

export const CHAT_DESTINATIONS = {
  plans: () => route(ROUTER_PATH.MEMBER),
  alpha_picks: () => route(ROUTER_PATH.INVEST_OPPORTUNITY),
  my_goals: () => route(ROUTER_PATH.PLAN_MY_GOALS),
  brief: () => route(ROUTER_PATH.BRIEF),
  checkup: () => route(ROUTER_PATH.PLAN_CHECKUP),
  desk: () => route(ROUTER_PATH.DESK),
  vault: () => route(ROUTER_PATH.VAULT),
} satisfies Record<string, () => ChatActionIntent>;

export type ChatDestinationId = keyof typeof CHAT_DESTINATIONS;

const CHAT_DESTINATION_IDS = Object.keys(CHAT_DESTINATIONS) as [
  ChatDestinationId,
  ...ChatDestinationId[],
];

export const CHAT_UI_CAPABILITIES: string[] = [
  "actions.v1",
  ...CHAT_DESTINATION_IDS.map((id) => `nav.${id}`),
  "layout.v1",
];

const MAX_CHAT_ACTIONS = 2;

export const chatActionZ = z.object({
  type: z.literal("navigate"),
  label: z.string().trim().min(1).max(40),
  destination: z.enum(CHAT_DESTINATION_IDS),
});

export type ChatAction = z.infer<typeof chatActionZ>;

export const actionButtonsPropsZ = z.object({
  actions: z.array(chatActionZ).min(1).max(MAX_CHAT_ACTIONS),
});

export interface ActionButtonsProps {
  actions: unknown[];
}

export interface ResolvedChatAction {
  label: string;
  actionType: ChatAction["type"];
  destination: ChatDestinationId;
  intent: ChatActionIntent;
}

export const resolveChatAction = (raw: unknown): ResolvedChatAction | null => {
  const parsed = chatActionZ.safeParse(raw);
  if (!parsed.success) {
    return null;
  }
  const action = parsed.data;
  return {
    label: action.label,
    actionType: action.type,
    destination: action.destination,
    intent: CHAT_DESTINATIONS[action.destination](),
  };
};

/** Drops malformed or unknown actions and keeps at most two; the first is primary. */
export const resolveChatActions = (raw: unknown): ResolvedChatAction[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(resolveChatAction)
    .filter((action): action is ResolvedChatAction => action !== null)
    .slice(0, MAX_CHAT_ACTIONS);
};
